import React from 'react';
import {
  AlertDescription,
  AlertTitle,
  AlertIcon,
  Alert,
  Box,
  Button,
  Divider,
  Flex,
  useModalContext,
} from '@chakra-ui/react';
import {BuilderCenteredContent} from '../BuilderCenteredContent';
import {AgentBuilderPageIndicator} from '../AgentBuilderPageIndicator';
import {BuilderFooter} from '../BuilderFooter';
import {useAgentNavigation} from '../AgentBuilderNav.state';
import {useAgentFields} from '../helpers';
import {
  useAgentBuilderServerState,
  useAgentBuilderStore,
} from '../AgentBuilder.state';
import {CreditsSection} from './CreditsSection';
import {FieldsSection} from './FieldsSection';
import {AccountSelectionSection} from './AccountSelectionSection';
import {AutoRefreshSection} from './AutoRefreshSection';
import {SignalsSection} from './SignalsSection';
import {useMutation} from '@tanstack/react-query';
import {usePublishAgent} from '../../../../hooks/api/agents';
import {StepContainer} from '../StepContainer';

export const AgentBuilderReviewSettingsStep = () => {
  const {isSaveable, isValid, saveChanges} = useAgentBuilderStore();
  const {id: agentId, status} = useAgentBuilderServerState();
  const {analystField, researcherField} = useAgentFields(agentId);
  const {onClose} = useModalContext();

  const onNavigate = useAgentNavigation({
    mutation: saveChanges,
    saveOnBack: status === 'draft' && isSaveable(),
  });

  const publishAgent = usePublishAgent();
  const saveAndExit = useMutation({
    mutationFn: async () => {
      await saveChanges.mutateAsync();
      if (status === 'draft') {
        await publishAgent.mutateAsync({agentId});
      }
    },
    onSuccess: onClose,
  });

  if (!analystField && !researcherField) {
    return (
      <Alert status="error">
        <AlertIcon />
        <Box>
          <AlertTitle>No agent fields found</AlertTitle>
          <AlertDescription>
            Please contact Keyplay support for help.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={saveChanges}>
      <Flex direction="column" flex="1">
        <BuilderCenteredContent maxW="650px">
          <AgentBuilderPageIndicator />
          <Flex direction="column" flex="1" gap="6" pb="8">
            <Box fontSize="xl">Review & Publish Your Agent</Box>
            <CreditsSection />
            <Divider />
            <FieldsSection />
            <Divider />
            <AccountSelectionSection />
            <Divider />
            <AutoRefreshSection />
            <Divider />
            <SignalsSection />
          </Flex>
        </BuilderCenteredContent>

        <Divider />

        <BuilderFooter justifyContent="space-between">
          <Button variant="ghost" onClick={() => onNavigate('prev')}>
            Back
          </Button>

          <Button
            colorScheme="kbuttonblue"
            isDisabled={!isValid()}
            isLoading={saveAndExit.isPending || saveChanges.isPending}
            onClick={() => saveAndExit.mutate()}
          >
            {status === 'published' ? 'Save & Exit' : 'Publish & Run Agent'}
          </Button>
        </BuilderFooter>
      </Flex>
    </StepContainer>
  );
};
